import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { child, getDatabase, get, push, ref, remove as removeNode, set, update, query, orderByChild, equalTo } from 'firebase/database';

import { firebaseConfig } from 'config/constants';
import { computeDuration } from 'lib/broadcast';
import { deviceCodeKey, normalizeDeviceCode } from 'lib/devices';
import { getDownloadURL, getStorage, ref as storageRef, deleteObject } from 'firebase/storage';

export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const database = getDatabase(app);

export const save = async (name, id, data) => {
  return set(ref(database, [name, id].join('/')), data)
}

export const create = async (name, data) => {
  const id = push(child(ref(database), name)).key
  data.id = id

  await update(ref(database), {
    [[name, id].join('/')]: data
  });

  return data
}

// Several paths in ONE atomic write: all of them are saved, or none.
// `updates` maps "node/id/field" paths to values (null deletes the field).
export const updateMany = async (updates) => {
  // Firebase rejects `undefined` (and NaN); a JSON round trip turns those into
  // missing fields / null.
  const clean = {}
  for (const [path, value] of Object.entries(updates)) {
    clean[path] = value === undefined ? null : JSON.parse(JSON.stringify(value))
  }
  return update(ref(database), clean)
}

// All records of a node that belong to a client, as an array (each with its `id`).
// Reads straight from the database, so it is never stale like the cached lists.
export const getAll = async (name, clientId) => {
  const snapshot = await get(query(ref(database, name), orderByChild('clientId'), equalTo(clientId)))
  return snapshot.exists()
    ? Object.entries(snapshot.val()).map(([key, value]) => ({ id: key, ...value }))
    : []
}

// Creates a broadcast whose `duration` is recalculated from the current content /
// broadcast data, instead of trusting the number the form remembered. When that data
// cannot be read, the duration from the form is kept.
export const createBroadcastRecord = async (record) => {
  const [broadcasts, contentItems] = await Promise.all([
    getAll('broadcast', record.clientId),
    getAll('content', record.clientId),
  ])
  const duration = computeDuration(record, { broadcasts, contentItems })
  return create('broadcast', duration === null ? { ...record } : { ...record, duration })
}

// Find a device with the given code within a client, or null. Used to keep device codes unique
// per client. The code is compared without surrounding spaces and ignoring upper / lower case
// ("MEGAGYM6" is "megagym6"). `exceptDeviceId` is the device being edited: it does not count.
// Reads straight from the database, so it is never stale like the cached lists.
export const findDeviceByCode = async (clientId, code, exceptDeviceId) => {
  const wanted = deviceCodeKey(code)
  if (!clientId || !wanted) {
    return null
  }

  const devices = await getAll('device', clientId)
  return devices.find(d => deviceCodeKey(d.code) === wanted && d.id !== exceptDeviceId) || null
}

const duplicateCodeError = (existing) => new Error(`A device with this code already exists (${existing.code}).`)

// When each device was created or last saved in the admin app ("Updated" on the Devices page):
// deviceUpdated/{clientId}/{deviceId} = { id, updated_at }. It is NOT a field of the device record:
// players released before 2.2.0 (most of the devices) write the whole device record from their
// own model on every ping, which drops every field that model does not know. The players never
// read or write this node. The client id in the path keeps the clients apart, like `clientId`.
const deviceUpdatedPath = (clientId, deviceId) => {
  if (!clientId || !deviceId) {
    throw new Error('Missing client or device id for the device update time.')
  }
  return `deviceUpdated/${clientId}/${deviceId}`
}
const deviceUpdatedEntry = (deviceId) => ({ id: deviceId, updated_at: new Date().toISOString() })

// What is stored of a device: without the fields the app only adds for showing it
// (`location`: hooks/useDevice; `updatedAt`: the time from deviceUpdated)
const deviceFields = ({ location, updatedAt, ...record }) => record

// Deletes a device and its update time in ONE write. Throws on failure (the dialog shows it).
export const removeDeviceRecord = async (device) => {
  if (!device?.id) {
    throw new Error('Missing device when removing!')
  }
  const updates = { [`device/${device.id}`]: null }
  if (device.clientId) {
    updates[deviceUpdatedPath(device.clientId, device.id)] = null
  }
  return updateMany(updates)
}

// Creates a device; its code is required and has to be unique within the client. The code is
// stored without the spaces around it.
export const createDeviceRecord = async (data) => {
  const code = normalizeDeviceCode(data.code)
  if (!code) {
    throw new Error('Device code is required.')
  }

  const existing = await findDeviceByCode(data.clientId, code)
  if (existing) {
    throw duplicateCodeError(existing)
  }

  // The device and the moment it was saved, in ONE write (a new push key, as `create` makes it)
  const id = push(child(ref(database), 'device')).key
  const created = { ...deviceFields(data), code, id }
  await updateMany({
    [`device/${id}`]: created,
    [deviceUpdatedPath(data.clientId, id)]: deviceUpdatedEntry(id),
  })

  // Two people saving the same code at the same moment can both pass the check above, and the
  // database rules cannot say "unique". So look again after writing: whoever finds another
  // device with this code takes their own back. (If both do, neither stays and both are told
  // so: safe, and only possible within a fraction of a second.)
  const clash = await findDeviceByCode(data.clientId, code, created.id)
  if (clash) {
    await updateMany({ [`device/${id}`]: null, [deviceUpdatedPath(data.clientId, id)]: null })
    throw duplicateCodeError(clash)
  }
  return created
}

// Saves an edited device. Only a CHANGED code has to be free: a device that keeps its code is
// not blocked by an older record that shares it. `previousCode` is the code before editing.
export const saveDeviceRecord = async (clientId, id, data, previousCode) => {
  const code = normalizeDeviceCode(data.code)
  if (!code) {
    throw new Error('Device code is required.')
  }

  const changed = deviceCodeKey(previousCode) !== deviceCodeKey(code)
  if (changed) {
    const existing = await findDeviceByCode(clientId, code, id)
    if (existing) {
      throw duplicateCodeError(existing)
    }
  }

  // The whole record (as `save` writes it) and the moment it was saved, in ONE write
  await updateMany({
    [`device/${id}`]: { ...deviceFields(data), code },
    [deviceUpdatedPath(clientId, id)]: deviceUpdatedEntry(id),
  })

  // The same second look as when creating; the old code is put back if somebody was faster
  if (changed) {
    const clash = await findDeviceByCode(clientId, code, id)
    if (clash) {
      await updateMany({ [`device/${id}/code`]: previousCode })
      throw duplicateCodeError(clash)
    }
  }
}

// A device of the client (other than `exceptDeviceId`) that already has this location, or
// null. The database rules cannot express "one device per location", so the forms check it
// here, against fresh data, right before saving.
export const findDeviceByLocation = async (clientId, locationId, exceptDeviceId) => {
  if (!clientId || !locationId) {
    return null
  }

  const devices = await getAll('device', clientId)
  return devices.find(d => d.locationId === locationId && d.id !== exceptDeviceId) || null
}

export const remove = async (name, id) => {
  if (!id) {
    throw new Error('Missing ID when removing:' + name)
  }
  return removeNode(ref(database, [name, id].join('/')))
}

// [key, record] pairs of a node that belong to a client. Unlike `getAll`, this keeps the
// real database key, which is what has to be used when a record is deleted.
const getEntries = async (name, clientId) => {
  const snapshot = await get(query(ref(database, name), orderByChild('clientId'), equalTo(clientId)))
  return snapshot.exists() ? Object.entries(snapshot.val()) : []
}

// Name of a broadcast for an error message (falls back to its id)
const broadcastName = (broadcastEntries, id) =>
  broadcastEntries.find(([key]) => key === id)?.[1].name || id

const joinNames = (names) => [...new Set(names)].join(', ')

// Deletes a broadcast, its multiplier copies and their schedules in ONE atomic write.
// Refused (with an Error) while a composite broadcast plays it, or is scheduled to.
export const removeBroadcast = async (broadcast) => {
  if (!broadcast) {
    throw new Error('Missing broadcast when removing!')
  }

  const [broadcasts, schedules] = await Promise.all([
    getEntries('broadcast', broadcast.clientId),
    getEntries('scheduledBroadcastUpdate', broadcast.clientId),
  ])

  const ids = [broadcast.id]
  for (const [key, item] of broadcasts) {
    if (item.sourceId && item.sourceId === broadcast.id) {
      ids.push(key)
    }
  }

  const listsIt = (list) => Array.isArray(list) && list.some(id => ids.includes(id))

  const used = broadcasts
    .filter(([, item]) => listsIt(item.broadcastList))
    .map(([, item]) => item.name)
  // A schedule of one of the deleted broadcasts goes away with it, so only the schedules
  // of OTHER broadcasts can still need it
  const scheduled = schedules
    .filter(([, item]) => !ids.includes(item.broadcastId) && listsIt(item.broadcastList))
    .map(([, item]) => broadcastName(broadcasts, item.broadcastId))

  const problems = []
  if (used.length > 0) {
    problems.push('Broadcast is used in composite broadcasts: ' + joinNames(used))
  }
  if (scheduled.length > 0) {
    problems.push('Broadcast is in the scheduled change of: ' + joinNames(scheduled))
  }
  if (problems.length > 0) {
    throw new Error(problems.join('. '))
  }

  const updates = {}
  for (const id of ids) {
    updates['broadcast/' + id] = null
  }
  for (const [key, item] of schedules) {
    if (ids.includes(item.broadcastId)) {
      updates['scheduledBroadcastUpdate/' + key] = null
    }
  }
  await updateMany(updates)
}

// Deletes the file, then the record. Refused (with an Error) while a broadcast plays the
// content, or a scheduled change of a broadcast is going to.
export const removeContent = async (content) => {
  if (!content) {
    throw new Error('Missing content when removing!')
  }

  const path = content.path || ('content/' + content.clientId + '/' + content.id + '/' + content.filename)

  const storage = getStorage()
  const fileRef = storageRef(storage, path)

  const [broadcasts, schedules] = await Promise.all([
    getEntries('broadcast', content.clientId),
    getEntries('scheduledBroadcastUpdate', content.clientId),
  ])

  const playsIt = (list) => Array.isArray(list) && list.includes(content.id)

  const used = broadcasts
    .filter(([, item]) => playsIt(item.contentList))
    .map(([, item]) => item.name)
  const scheduled = schedules
    .filter(([, item]) => playsIt(item.contentList))
    .map(([, item]) => broadcastName(broadcasts, item.broadcastId))

  const problems = []
  if (used.length > 0) {
    problems.push('Content is used in broadcasts: ' + joinNames(used))
  }
  if (scheduled.length > 0) {
    problems.push('Content is in the scheduled change of: ' + joinNames(scheduled))
  }
  if (problems.length > 0) {
    throw new Error(problems.join('. '))
  }

  // Delete the file first: if that fails the record stays, so the user can retry. A
  // file that is already gone is not an error.
  try {
    await deleteObject(fileRef)
  } catch (error) {
    if (error.code !== 'storage/object-not-found') {
      throw error
    }
  }

  // The record and the marker the Cloud Function keeps for its transcoder job go together
  await updateMany({
    ['content/' + content.id]: null,
    ['transcodeJob/' + content.id]: null,
  })
}

export const getContentURL = async (content) => {
  const path = content.path || ('content/' + content.clientId + '/' + content.id + '/' + content.filename)

  const storage = getStorage()
  const fileRef = storageRef(storage, path)

  return getDownloadURL(fileRef)
}

export default app;

